import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<F extends string> {
  field: F;
  direction: SortDirection;
}

/**
 * Comparator for a single sortable column. Receives the active direction so
 * implementations can keep null/empty values pinned to the end regardless of
 * direction (see `byKey`), rather than letting the hook negate the result and
 * flip nulls to the top.
 */
export type Comparator<T> = (a: T, b: T, direction: SortDirection) => number;

interface ByKeyOptions {
  /** Compare numerically instead of via localeCompare. */
  numeric?: boolean;
}

/**
 * Build a {@link Comparator} that sorts by a derived key. null/undefined/'' are
 * always sorted last, in both directions. Strings use localeCompare; pass
 * `{ numeric: true }` for numeric fields.
 */
export function byKey<T>(
  get: (item: T) => string | number | null | undefined,
  options: ByKeyOptions = {},
): Comparator<T> {
  return (a, b, direction) => {
    const av = get(a);
    const bv = get(b);
    const aEmpty = av == null || av === '';
    const bEmpty = bv == null || bv === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1; // a after b, always
    if (bEmpty) return -1; // b after a, always

    const base = options.numeric
      ? (av as number) - (bv as number)
      : String(av).localeCompare(String(bv));
    return direction === 'asc' ? base : -base;
  };
}

export interface UseClientTableSortResult<T, F extends string> {
  sorted: T[];
  sortField: F;
  sortDirection: SortDirection;
  /** Toggle direction when re-selecting the active field, else sort that field ascending. */
  handleSort: (field: F) => void;
}

/**
 * Client-side sorting for a loaded list. Clicking a column toggles asc/desc;
 * selecting a new column starts ascending. `comparators` must be referentially
 * stable (define it at module scope) so the memoized sort isn't invalidated
 * every render.
 */
export function useClientTableSort<T, F extends string>(
  data: T[],
  comparators: Record<F, Comparator<T>>,
  initial: SortState<F>,
  options?: { onSortChange?: (next: SortState<F>) => void },
): UseClientTableSortResult<T, F> {
  const [sort, setSort] = useState<SortState<F>>(initial);
  const onSortChange = options?.onSortChange;

  const handleSort = useCallback(
    (field: F) => {
      // Compute `next` outside the state updater so the onSortChange side effect
      // fires exactly once — a setState updater must be pure, and React
      // StrictMode double-invokes it in dev (which would double-persist).
      const next: SortState<F> =
        sort.field === field
          ? { field, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
          : { field, direction: 'asc' };
      setSort(next);
      onSortChange?.(next);
    },
    [sort, onSortChange],
  );

  const sorted = useMemo(() => {
    const comparator = comparators[sort.field];
    if (!comparator) return data;
    return [...data].sort((a, b) => comparator(a, b, sort.direction));
  }, [data, comparators, sort]);

  return {
    sorted,
    sortField: sort.field,
    sortDirection: sort.direction,
    handleSort,
  };
}
